import type { FulfillmentStatusKey } from '@/types/quote';
import type { ApprovalRejection, QuoteStatus, QuoteSummary } from '@/hooks/useQuotes';

interface QuoteStatusMeta {
  label: string;
  toneKey: string;
}

export const QUOTE_STATUS_META: Record<string, QuoteStatusMeta> = {
  Draft: { label: 'Draft', toneKey: 'draft' },
  PendingApproval: { label: 'Pending Approval', toneKey: 'pending' },
  Approved: { label: 'Approved', toneKey: 'approved' },
  Submitted: { label: 'Submitted', toneKey: 'sent' },
  Sent: { label: 'Sent', toneKey: 'sent' },
  Accepted: { label: 'Accepted', toneKey: 'won' },
  Declined: { label: 'Declined', toneKey: 'lost' },
  Expired: { label: 'Expired', toneKey: 'expired' },
  Revoked: { label: 'Revoked', toneKey: 'revoked' },
  Converted: { label: 'Converted', toneKey: 'won' },
};

export const CHANGES_REQUESTED_LABEL = 'Changes requested';

export function quoteStatusBadge(
  status: string,
  changesRequested?: ApprovalRejection | null,
): { value: string; label: string } {
  if (status === 'Draft' && changesRequested) return { value: 'changesrequested', label: CHANGES_REQUESTED_LABEL };
  const meta = Object.prototype.hasOwnProperty.call(QUOTE_STATUS_META, status)
    ? QUOTE_STATUS_META[status]
    : undefined;
  if (meta) return { value: meta.toneKey, label: meta.label };
  return { value: 'draft', label: status.trim() || 'Unknown' };
}

export function canCreateQuoteVersion(status: QuoteStatus): boolean {
  return status === 'Revoked' || status === 'Declined' || status === 'Expired';
}

export const TYPE_LABELS: Record<string, string> = {
  NewBusiness: 'New',
  Renewal: 'Renewal',
  RenewalWithChanges: 'Changes',
  Amendment: 'Amend',
  ProofOfConcept: 'Proof of Concept',
};

export const CANCELLATION_TYPE_LABEL = 'Cancellation';

// A quote that only ends subscriptions is saved as an Amendment (the HubSpot Churn deal type maps
// there), but reads as what it does.
export function quoteTypeLabel(quote: Pick<QuoteSummary, 'quoteType' | 'cancelOnly'>): string {
  if (quote.cancelOnly) return CANCELLATION_TYPE_LABEL;
  return TYPE_LABELS[quote.quoteType] ?? quote.quoteType;
}

// The lifecycle chips on the quotes list. `fulfillmentStatus` filters on a different axis than
// `statuses`: a manual quote awaiting hand provisioning is already Accepted or Converted, so a status
// list alone cannot single it out. Viewed narrows Sent to quotes the customer has opened.
export const QUOTE_WORKFLOW_TABS = [
  { value: 'all', label: 'All', statuses: [] },
  { value: 'draft', label: 'Draft', statuses: ['Draft'] },
  { value: 'awaiting-approval', label: 'Awaiting approval', statuses: ['PendingApproval'] },
  { value: 'ready-to-send', label: 'Ready to send', statuses: ['Approved', 'Submitted'] },
  { value: 'sent', label: 'Sent', statuses: ['Sent'] },
  { value: 'viewed', label: 'Viewed', statuses: ['Sent'], viewed: true },
  { value: 'accepted', label: 'Accepted', statuses: ['Accepted', 'Converted'] },
  { value: 'closed', label: 'Expired / Declined', statuses: ['Expired', 'Declined', 'Revoked'] },
  {
    value: 'awaiting-fulfillment',
    label: 'Awaiting fulfillment',
    statuses: [],
    fulfillmentStatus: 'AwaitingFulfillment',
  },
  { value: 'archived', label: 'Archived', statuses: [], archived: true },
] as const;

// Queues few people open: they sit behind a More chip so the lifecycle chips fit on one row.
export const QUOTE_WORKFLOW_MORE_TABS: readonly string[] = ['awaiting-fulfillment', 'archived'];

// Links and bookmarks made before the lifecycle chips still land on the matching chip. Needs action
// was Draft plus Ready to send; a draft is the commoner of the two, and the pipeline link that used
// it also carries the exact status.
const LEGACY_WORKFLOW_TABS: Record<string, string> = {
  'needs-action': 'draft',
  'with-customer': 'sent',
  won: 'accepted',
  'not-proceeding': 'closed',
};

export function resolveWorkflowTab(value: string | null | undefined, canManage: boolean): string {
  const resolved = value ? LEGACY_WORKFLOW_TABS[value] ?? value : 'all';
  const known = QUOTE_WORKFLOW_TABS.some((tab) => tab.value === resolved);
  if (!known || (resolved === 'archived' && !canManage)) return 'all';
  return resolved;
}

function workflowTab(value: string) {
  return QUOTE_WORKFLOW_TABS.find((candidate) => candidate.value === value);
}

export function workflowStatuses(value: string): string | undefined {
  const tab = workflowTab(value);
  return tab && tab.statuses.length > 0 ? tab.statuses.join(',') : undefined;
}

export function workflowViewed(value: string): boolean | undefined {
  const tab = workflowTab(value);
  return tab && 'viewed' in tab ? tab.viewed : undefined;
}

export function workflowFulfillmentStatus(value: string): FulfillmentStatusKey | undefined {
  const tab = workflowTab(value);
  return tab && 'fulfillmentStatus' in tab ? tab.fulfillmentStatus : undefined;
}

export function workflowArchived(value: string): boolean | undefined {
  const tab = workflowTab(value);
  return tab && 'archived' in tab ? tab.archived : undefined;
}
